import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { 
  generateMultichoiceQuestions, 
  generateMultichoiceOptions 
} from '../../../services/aiExerciseService';
import VoiceSelector from '../../common/VoiceSelector';
import LearningMaterialContent from '../../LearningMaterial/LearningMaterialContent';
import learningMaterialService, { LearningMaterial } from '../../../services/learningMaterialService';
import { htmlToPlainText } from '../materialConfigUtils';

interface MultichoiceOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface MultichoiceQuestion {
  id: string;
  question: string;
  options: MultichoiceOption[];
  explanation?: string;
}

interface MaterialBasedMultichoiceConfigProps {
  onConfigChange: (config: any) => void;
  initialConfig?: any;
  lessonId: number;
  exerciseType?: 'material_based_multichoice' | 'reading_comprehension_multichoice' | 'true_false';
  generationProfile?: 'standard' | 'reading_comprehension_fi' | 'true_false';
  fixedOptionCount?: number;
}

const optionIdForIndex = (index: number) => String.fromCharCode(97 + index);

const buildOptions = (optionCount: number, trueFalseMode: boolean): MultichoiceOption[] => (
  Array.from({ length: optionCount }, (_, index) => ({
    id: optionIdForIndex(index),
    text: trueFalseMode ? (index === 0 ? 'Oikein' : 'Väärin') : '',
    isCorrect: index === 0,
  }))
);

const MaterialBasedMultichoiceConfig: React.FC<MaterialBasedMultichoiceConfigProps> = ({
  onConfigChange,
  initialConfig = {},
  lessonId,
  exerciseType = 'material_based_multichoice',
  generationProfile = 'standard',
  fixedOptionCount,
}) => {
  const [materials, setMaterials] = useState<LearningMaterial[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(true);
  const [questions, setQuestions] = useState<MultichoiceQuestion[]>(initialConfig.questions || []);
  const [customPrompt, setCustomPrompt] = useState<string>(initialConfig.customPrompt || '');
  const [allowMultipleSelection, setAllowMultipleSelection] = useState<boolean>(
    initialConfig.allowMultipleSelection ?? false
  );
  const [showMaterialToStudent, setShowMaterialToStudent] = useState<boolean>(initialConfig.showMaterialToStudent ?? false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | ''>(initialConfig.materialId ?? '');
  const [selectedVoiceCode, setSelectedVoiceCode] = useState<string>(initialConfig.voiceCode || 'fi-FI-NooraNeural');
  const [questionCount, setQuestionCount] = useState<number>(3);
  const [optionCount, setOptionCount] = useState<number>(fixedOptionCount || initialConfig.optionCount || 4);
  
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState<string>('');
  const trueFalseMode = generationProfile === 'true_false';

  useEffect(() => {
    const loadMaterials = async () => {
      setIsLoadingMaterials(true);
      try {
        const data = await learningMaterialService.getMaterialsByLesson(lessonId);
        setMaterials(data);
      } catch (error) {
        console.error('Failed to load learning materials:', error);
      } finally {
        setIsLoadingMaterials(false);
      }
    };

    loadMaterials();
  }, [lessonId]);

  const selectedMaterial = useMemo(
    () => materials.find((material) => material.id === selectedMaterialId),
    [materials, selectedMaterialId],
  );

  const getPlainText = () => {
    if (selectedMaterial) {
      return htmlToPlainText(selectedMaterial.content);
    }

    return '';
  };

  const updateConfig = (updatedQuestions?: MultichoiceQuestion[], overrides: Record<string, unknown> = {}) => {
    const questionsToUse = updatedQuestions || questions;
    onConfigChange({
      exerciseType,
      generationProfile,
      materialId: selectedMaterialId || undefined,
      customPrompt,
      questions: questionsToUse,
      optionCount,
      allowMultipleSelection: trueFalseMode ? false : allowMultipleSelection,
      voiceCode: selectedVoiceCode,
      showMaterialToStudent,
      ...overrides,
    });
  };

  const addQuestion = () => {
    const newQuestion: MultichoiceQuestion = {
      id: `q_${Date.now()}`,
      question: '',
      options: buildOptions(optionCount, trueFalseMode),
      explanation: '',
    };
    const updatedQuestions = [...questions, newQuestion];
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions);
  };

  const handleOptionCountChange = (newOptionCount: number) => {
    setOptionCount(newOptionCount);
    const updatedQuestions = questions.map((question) => ({
      ...question,
      options: Array.from({ length: newOptionCount }, (_, index) => ({
        id: optionIdForIndex(index),
        text: trueFalseMode
          ? (index === 0 ? 'Oikein' : 'Väärin')
          : question.options[index]?.text || '',
        isCorrect: question.options[index]?.isCorrect || false,
      })),
    }));
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions, { optionCount: newOptionCount });
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions);
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions);
  };

  const updateOption = (questionIndex: number, optionIndex: number, field: string, value: any) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      [field]: value,
    };
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions);
  };

  const generateAIQuestions = async () => {
    if (!selectedMaterial) {
      return;
    }

    try {
      const generatedQuestions = await generateMultichoiceQuestions(
        getPlainText(),
        questionCount,
        customPrompt,
        generationProfile,
        optionCount,
      );
      const newQuestions = [...questions, ...generatedQuestions.data];
      setQuestions(newQuestions);
      updateConfig(newQuestions);
      setGenerateDialogOpen(false);
    } catch (error) {
      console.error('Error generating questions:', error);
    }
  };

  const generateAIOptions = async () => {
    if (currentQuestionIndex === -1 || !newCorrectAnswer) return;

    try {
      const generatedOptions = await generateMultichoiceOptions(
        getPlainText(),
        questions[currentQuestionIndex].question,
        newCorrectAnswer,
        optionCount
      );
      
      updateQuestion(currentQuestionIndex, 'options', generatedOptions.data);
      setOptionsDialogOpen(false);
      setNewCorrectAnswer('');
    } catch (error) {
      console.error('Error generating options:', error);
    }
  };

  const clearTextAndQuestions = () => {
    setQuestions([]);
    setCustomPrompt('');
    setShowMaterialToStudent(false);
    updateConfig([], { customPrompt: '', showMaterialToStudent: false });
  };

  useEffect(() => {
    if (selectedMaterialId === '' && initialConfig.materialId) {
      setSelectedMaterialId(initialConfig.materialId);
    }
  }, [initialConfig.materialId, selectedMaterialId]);

  useEffect(() => {
    updateConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterialId, selectedVoiceCode, allowMultipleSelection, showMaterialToStudent]);

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Oppimateriaali
        </Typography>

        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="material-multichoice-select-label">Materiaali</InputLabel>
          <Select
            labelId="material-multichoice-select-label"
            value={selectedMaterialId}
            label="Materiaali"
            onChange={(event) => setSelectedMaterialId(event.target.value as number)}
            disabled={isLoadingMaterials}
          >
            {materials.map((material) => (
              <MenuItem key={material.id} value={material.id}>
                {material.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {materials.length === 0 && !isLoadingMaterials && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Oppitunnille ei ole vielä luotu materiaalia. Luo materiaali ennen tämän tehtävätyypin tallennusta.
          </Alert>
        )}

        {selectedMaterial && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedMaterial.title}
            </Typography>
            <LearningMaterialContent material={selectedMaterial as any} />
          </Box>
        )}

        <Button
          variant="text"
          startIcon={<ClearAllIcon />}
          onClick={clearTextAndQuestions}
          sx={{ mt: 2 }}
        >
          Tyhjennä kysymykset
        </Button>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Asetukset
        </Typography>

        {generationProfile === 'reading_comprehension_fi' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Kysymykset ja palaute luodaan suomeksi. Vastausvaihtoehdot perustuvat materiaalin kieleen.
          </Alert>
        )}

        {trueFalseMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Jokaisessa väittämässä on aina vaihtoehdot Oikein ja Väärin. Opiskelija valitsee yhden vastauksen.
          </Alert>
        )}
        
        <TextField
          label="Lisäohje tekoälylle"
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          value={customPrompt}
          onChange={(e) => {
            setCustomPrompt(e.target.value);
            updateConfig(questions, { customPrompt: e.target.value });
          }}
          sx={{ mb: 2 }}
        />

        {!trueFalseMode && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allowMultipleSelection}
                onChange={(e) => {
                  setAllowMultipleSelection(e.target.checked);
                  updateConfig(questions, { allowMultipleSelection: e.target.checked });
                }}
              />
            }
            label="Salli useamman vaihtoehdon valinta"
            sx={{ mb: 2 }}
          />
        )}

        {!fixedOptionCount && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="multichoice-option-count-label">Vastausvaihtoehtojen määrä</InputLabel>
            <Select
              labelId="multichoice-option-count-label"
              label="Vastausvaihtoehtojen määrä"
              value={optionCount}
              onChange={(event) => handleOptionCountChange(Number(event.target.value))}
            >
              {[2, 3, 4, 5, 6].map((count) => (
                <MenuItem key={count} value={count}>{count}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={showMaterialToStudent}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowMaterialToStudent(newValue);
                updateConfig(questions, { showMaterialToStudent: newValue });
              }}
            />
          }
          label="Näytä materiaali opiskelijalle"
          sx={{ mb: 2 }}
        />

        <VoiceSelector
          onSelectVoice={(voice) => {
            setSelectedVoiceCode(voice.code);
            updateConfig(questions, { voiceCode: voice.code });
          }}
          initialVoiceCode={selectedVoiceCode}
        />
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Monivalintakysymykset</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={() => setGenerateDialogOpen(true)}
              disabled={!selectedMaterial}
              sx={{ mr: 1 }}
            >
              Luo tekoälyllä
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={addQuestion}
            >
              Lisää kysymys
            </Button>
          </Box>
        </Box>

        {questions.map((question, questionIndex) => (
          <Paper key={question.id} elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="subtitle1">Kysymys {questionIndex + 1}</Typography>
              <Box>
                {!trueFalseMode && (
                  <Button
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => {
                      setCurrentQuestionIndex(questionIndex);
                      setOptionsDialogOpen(true);
                    }}
                  >
                    Luo vaihtoehdot
                  </Button>
                )}
                <IconButton onClick={() => removeQuestion(questionIndex)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <TextField
              label="Kysymys"
              variant="outlined"
              fullWidth
              value={question.question}
              onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vastausvaihtoehdot:
            </Typography>

            {question.options.map((option, optionIndex) => (
              <Box key={option.id} display="flex" alignItems="center" mb={1}>
                <Checkbox
                  checked={option.isCorrect}
                  onChange={(e) => updateOption(questionIndex, optionIndex, 'isCorrect', e.target.checked)}
                />
                <TextField
                  label={`Vaihtoehto ${option.id.toUpperCase()}`}
                  variant="outlined"
                  fullWidth
                  value={option.text}
                  onChange={(e) => updateOption(questionIndex, optionIndex, 'text', e.target.value)}
                  disabled={trueFalseMode}
                />
                {option.isCorrect && <Chip label="Oikea" color="success" size="small" sx={{ ml: 1 }} />}
              </Box>
            ))}

            <TextField
              label="Selitys (valinnainen)"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={question.explanation || ''}
              onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
              sx={{ mt: 2 }}
            />
          </Paper>
        ))}
      </Paper>

      {/* Generate Questions Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)}>
        <DialogTitle>Luo kysymyksiä tekoälyllä</DialogTitle>
        <DialogContent>
          <TextField
            label="Kysymysten määrä"
            type="number"
            value={questionCount}
            onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Peruuta</Button>
          <Button onClick={generateAIQuestions} variant="contained">
            Luo kysymykset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Options Dialog */}
      <Dialog open={optionsDialogOpen} onClose={() => setOptionsDialogOpen(false)}>
        <DialogTitle>Luo vastausvaihtoehtoja</DialogTitle>
        <DialogContent>
          <TextField
            label="Oikea vastaus"
            variant="outlined"
            fullWidth
            value={newCorrectAnswer}
            onChange={(e) => setNewCorrectAnswer(e.target.value)}
            sx={{ mt: 1 }}
            helperText="Anna oikea vastaus, niin tekoäly luo sille harhauttavia vaihtoehtoja"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDialogOpen(false)}>Peruuta</Button>
          <Button 
            onClick={generateAIOptions} 
            variant="contained"
            disabled={!newCorrectAnswer}
          >
            Luo vaihtoehdot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialBasedMultichoiceConfig;